//src/app/features/questionnaires/components/PreviewTable/index.tsx
import { useGetSpecializationsQuery } from 'app/services/accountant';
import { useGetQuestionnaireByIdQueryState } from 'app/services/questionnaires';
import { useGetUserBusinessByIdQuery } from 'app/services/user';
import { USDate } from 'Components/Dates/USDate';
import { DateRangeType } from 'Components/Form/DateInput';
import { QuestionnaireDocument } from 'Components/Form/UploadInput/UploadForm';
import { Grid, GridCell, GridRow, TGridHeader } from 'Components/Grid';
import { countriesLookUp } from 'countries';
import { isArray, isFunction } from 'lodash';
import { createContext, Fragment, ReactNode, useContext } from 'react';
import { useParams } from 'react-router-dom';
import { statesLookup } from 'states';
import { getLabel } from 'utils';

export function getPreviewFields(
  fields: IBaseField<any>[],
  value: any,
): IBaseField<any>[] {
  if (fields.length === 0) {
    return [];
  }
  const firstField = getPreviewSingleField(fields[0], value);
  const restFields = getPreviewFields(fields.slice(1), value);
  if (firstField) {
    return [firstField, ...restFields].flat();
  } else {
    return [...restFields].flat();
  }
}

function getPreviewSingleField(
  field: IBaseField<any>,
  value: any,
): IBaseField<any> | IBaseField<any>[] {
  // field has nested fields
  const dependencies = field.dependencies || [];

  if (dependencies && dependencies.length > 0) {
    // Only filter the dependencies with predicate that match the dependant field
    const allowedDependencies = dependencies.filter((dependency) => {
      return dependency.predicate
        ? dependency.predicate(value[field.name])
        : false;
    });
    return [field, ...getPreviewFields(allowedDependencies, value)];
  } else {
    return field;
  }
}
export interface IPreviewTableProps {
  fields: IBaseField<any>[];
  fieldsState: { values: any };
  sectionName: string;
}
const PreviewQuestionnaireContext = createContext<{
  questionnaireYear?: string;
}>({});

export default function PreviewTable(props: IPreviewTableProps) {
  const { fields, fieldsState, sectionName } = props;

  // return null;

  const { questionnaireId } = useParams();
  const { data: questionnaireData } = useGetQuestionnaireByIdQueryState(
    questionnaireId!,
  );
  const questionnaireYear = questionnaireData?.questionnaire.year;
  return (
    <PreviewQuestionnaireContext.Provider value={{ questionnaireYear }}>
      <KeyValueGrid
        headerClassName="bg-green-light"
        sectionTitle={{ render: sectionName }}
        data={getPreviewFields(
          nonInformationFields(fields),
          fieldsState.values,
        )}
        value={fieldsState.values}
      />
    </PreviewQuestionnaireContext.Provider>
  );
}

function nonInformationFields(fields: IBaseField<any>[]) {
  return fields.filter(({ type }) => type !== 'information');
}

function NotAvailable() {
  return <span className="text-gray-light">N/A</span>;
}
function PreviewVField({
  field,
  value,
}: {
  field: IBaseField<any>;
  value: any | any[];
}): JSX.Element {
  const { type } = field;
  let formatted: ReactNode;

  if (
    value === undefined ||
    value === '' ||
    value === null ||
    (isArray(value) && value.length === 0)
  ) {
    return <></>;
  }
  if (type === 'fieldArray' && isArray(value)) {
    return (
      <GridRow hover={false}>
        <GridCell fillRow={true}>
          <div className="font-semibold text-gray-dark ">
            <FieldLabel field={field} />
          </div>

          {value.map((record: any, recordIndex: number) => (
            <GridRow hover={false} key={recordIndex}>
              <GridCell fillRow={true}>
                <KeyValueGrid
                  headerClassName="bg-gray-light"
                  sectionTitle={{ render: recordIndex + 1 }}
                  value={record}
                  data={getPreviewFields(
                    nonInformationFields(field.dependencies || []),
                    record,
                  )}
                />
              </GridCell>
            </GridRow>
          ))}
        </GridCell>
      </GridRow>
    );
  }

  if (type === 'date') {
    formatted = <USDate date={value} />;
  }
  if (type === 'multiSelect') {
    formatted = value?.join(' - ');
  }
  if (type === 'checkbox') {
    formatted = value === true ? 'Checked' : 'N/A';
  }
  if (type === 'upload') {
    const renderDoc = (doc: QuestionnaireDocument) => (
      <a
        key={doc.url}
        target="_blank"
        className="text-green hover:underline"
        href={`${process.env.REACT_APP_URL}/${doc?.url}`}
      >
        {doc?.title}
      </a>
    );

    if (value.files) {
      formatted = (
        <div className="flex gap-3 flex-wrap">{value.files.map(renderDoc)}</div>
      );
    }
    if (!value.files && value.url) {
      formatted = renderDoc(value);
    }
  }
  if (type === 'country') {
    formatted = countriesLookUp[value].label;
  }
  if (type === 'multi-country') {
    if (isArray(value)) {
      formatted = value
        ?.map((country: string) => countriesLookUp[country].label)
        .join(' - ');
    } else {
      formatted = countriesLookUp[value]?.label;
    }
  }
  if (type === 'state') {
    formatted = statesLookup[value]?.label;
  }

  if (type === 'multi-state' && isArray(value)) {
    if (isArray(value)) {
      formatted = value
        ?.map((state: string) => statesLookup[state]?.label)
        .filter((x: string) => !!x)
        .join(' - ');
    } else {
      formatted = countriesLookUp[value]?.label;
    }
  }

  if (type === 'specialization') {
    formatted = <SpecicalizationPreview value={value} />;
  }

  if (type === 'dateRange') {
    formatted = (
      <div className="flex gap-2">
        <USDate date={value.from} /> - <USDate date={value.to} />
      </div>
    );
  }
  if (type === 'business') {
    formatted = <BusinessPreview value={value} />;
  }

  if (type === 'dateRanges') {
    formatted = (
      <div className="">
        {(value as DateRangeType[])
          .filter((r) => r.from || r.to)
          .map((range, index) => (
            <div key={index.toString()} className="flex gap-2 font-mono">
              <USDate date={range.from} /> - <USDate date={range.to} />
            </div>
          ))}
      </div>
    );
  }

  return <KeyValueRow field={field} value={formatted || value} />;
}

function SpecicalizationPreview({ value }: { value: string }) {
  const { data: specializations } = useGetSpecializationsQuery();

  return <>{(specializations || [])?.find((s) => s.value === value)?.label}</>;
}

function BusinessPreview({ value }: { value: string }) {
  const { data } = useGetUserBusinessByIdQuery(value);

  return <>{data?.business.name}</>;
}

export function KeyValueGrid({
  sectionTitle,
  data,
  headerClassName,
  value,
}: {
  value: any;
  sectionTitle: TGridHeader;
  data: IBaseField<any>[];
  headerClassName?: string;
}) {
  return (
    <div className="mt-1">
      <Grid
        headerClassName={headerClassName}
        columns={[sectionTitle, '']}
        data={data.map((f) => ({ id: f.name, ...f }))}
      >
        {(field) => {
          return <PreviewVField field={field} value={value[field.name]} />;
        }}
      </Grid>
    </div>
  );
}

function FieldLabel({ field }: { field: IBaseField<any> }) {
  const questionnaireYear = useContext(
    PreviewQuestionnaireContext,
  ).questionnaireYear;
  const label = isFunction(field.label)
    ? field.label({ year: questionnaireYear })
    : getLabel(undefined, field.label, questionnaireYear);
  return <div className="text-gray-700 font-semibold">{label}</div>;
}

function FieldValue({ value }: { value: any }) {
  return (
    <div className="text-gray-dark font-semibold flex flex-wrap break-all break-words">
      {value}
    </div>
  );
}
function KeyValueRow({ field, value }: { field: IBaseField<any>; value: any }) {
  return (
    <GridRow>
      <GridCell>
        <FieldLabel field={field} />
      </GridCell>
      <GridCell>
        <FieldValue value={value} />
      </GridCell>
    </GridRow>
  );
}
